<script setup>
import { ref } from 'vue'

const props = defineProps({
  golfInfo: {
    type: Object,
    required: true
  }
})
const imageList = [
  { href: '1.webp', num: '01' },
  { href: '3.webp', num: '03' },
  { href: '4.webp', num: '04' }
]

const slideStatus = ref('01')
const imageErrors = ref(Array(imageList.length).fill(false))
const checkImageExists = async (url, index) => {
  const response = await fetch(url, { method: 'HEAD' })
  if (!response.ok) {
    imageErrors.value[index] = true
  }
}
imageList.forEach((item, index) => {
  const url = `https://gvs.weathernews.jp/golf/image/golf_info_image/golf_${props.golfInfo.id}/${item.href}`
  checkImageExists(url, index)
})
const allImagesFailed = computed(() => {
  return imageErrors.value.every((error) => error === true)
})
const slide = (num) => {
  slideStatus.value = num
}
</script>

<template>
  <section class="golfInfo">
    <h2 class="heading">
      詳細情報
    </h2>
    <div class="block">
      <h3 class="name">
        {{ golfInfo.name.replace(/[（/]旧.*$/, '') }}
      </h3>
      <template v-if="allImagesFailed">
        <div class="image">
          <div class="slide">
            <figure
              class="isSelected"
            >
              <img
                src="https://gvs.weathernews.jp/golf/image/golf_info_image/noimage.webp"
                alt=""
              >
            </figure>
          </div>
        </div>
      </template>
      <template v-if="!allImagesFailed">
        <div class="image">
          <div class="slide">
            <figure
              v-for="(item, index) in imageList"
              :key="index"
              :class="item.num === slideStatus && 'isSelected'"
            >
              <img
                :src="`https://gvs.weathernews.jp/golf/image/golf_info_image/golf_${golfInfo.id}/${item.href}`"
                alt=""
              >
            </figure>
          </div>
          <div class="thumbnail">
            <div
              v-for="(item, index) in imageList"
              :key="index"
            >
              <figure
                v-show="!imageErrors[index]"
                :class="item.num === slideStatus && 'isSelected'"
                @click="slide(item.num)"
              >
                <img
                  :src="`https://gvs.weathernews.jp/golf/image/golf_info_image/golf_${golfInfo.id}/${item.href}`"
                  alt=""
                >
              </figure>
            </div>
          </div>
        </div>
      </template>
    </div>
    <div class="block">
      <h3 class="title">
        基本情報
      </h3>
      <div class="table">
        <div class="row">
          <p class="item">
            所在地
          </p>
          <p class="text">
            {{ `${golfInfo.postal || '--'}\n${golfInfo.address || '--'}` }}
          </p>
        </div>
        <div class="row">
          <p class="item">
            連絡先
          </p>
          <p class="text">
            {{ `${golfInfo.telephone || '--'}` }}
          </p>
        </div>
        <div class="row">
          <p class="item">
            URL
          </p>
          <p
            class="text"
            data-type="url"
          >
            {{ `${golfInfo.url || '--'}` }}
          </p>
        </div>
        <div class="row">
          <p class="item">
            交通手段
          </p>
          <p class="text">
            {{ `${golfInfo.access || '--'}` }}
          </p>
        </div>
        <div class="row">
          <p class="item">
            カード
          </p>
          <p class="text">
            {{ `${golfInfo.credit || '--'}` }}
          </p>
        </div>
        <div class="row">
          <p class="item">
            予約
          </p>
          <p class="text">
            {{ `${golfInfo.how_to_reserve || '--'}` }}
          </p>
        </div>
        <div class="row">
          <p class="item">
            休日
          </p>
          <p class="text">
            {{ `${golfInfo.holiday || '--'}` }}
          </p>
        </div>
      </div>
      <h3 class="title">
        施設・サービス情報
      </h3>
      <div class="table">
        <div class="row">
          <p class="item">
            ホール数
          </p>
          <p class="text">
            {{ `${String(golfInfo.number_of_hole) || '--'}` }}
          </p>
        </div>
        <div class="row">
          <p class="item">
            パー
          </p>
          <p class="text">
            {{ `${String(golfInfo.par) || '--'}` }}
          </p>
        </div>
        <div class="row">
          <p class="item">
            レート
          </p>
          <p class="text">
            {{ `${String(golfInfo.course_rate) || '--'}` }}
          </p>
        </div>
        <div class="row">
          <p class="item">
            コース
          </p>
          <p class="text">
            {{ `${golfInfo.course || '--'}` }}
          </p>
        </div>
        <div class="row">
          <p class="item">
            コース状況
          </p>
          <p class="text">
            {{ `${golfInfo.state_of_course || '--'}` }}
          </p>
        </div>
        <div class="row">
          <p class="item">
            コース面積
          </p>
          <p class="text">
            {{ `${String(golfInfo.dimensions) || '--'}㎡` }}
          </p>
        </div>
        <div class="row">
          <p class="item">
            グリーン状況
          </p>
          <p class="text">
            {{ `${golfInfo.state_of_green || '--'}` }}
          </p>
        </div>
        <div class="row">
          <p class="item">
            距離
          </p>
          <p class="text">
            {{ `${String(golfInfo.yardage) || '--'}Y` }}
          </p>
        </div>
        <div class="row">
          <p class="item">
            練習場
          </p>
          <p class="text">
            {{ `${golfInfo.golf_range || '--'}` }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.golfInfo {
  display: flex;
  flex-direction: column;
  gap: 20px;
  .heading {
    padding-left: 15px;
    border-left: solid 5px #c6ccda;
    color: #000;
    font-size: 18px;
    font-weight: 600;
    line-height: 25px;
    letter-spacing: .25px;
    @media screen and (min-width: 830px) {
      font-size: 22px;
    }
  }
  .block {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  .name {
    color: #000;
    font-size: 18px;
    font-weight: 600;
    line-height: 22px;
    letter-spacing: .25px;
  }
  .image {
    display: flex;
    flex-direction: column;
  }
  .slide {
    position: relative;
    display: flex;
    justify-content: center;
    figure {
      display: none;
      max-width: 310px;
      aspect-ratio: 31 / 18;
      &.isSelected {
        display: block;
      }
      img {
        width: 100%;
        object-fit: contain;
      }
    }
  }
  .thumbnail {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 0;
    figure {
      display: flex;
      justify-content: center;
      height: 50px;
      width: calc((42px * 31 / 18) + 8px);
      border: solid 4px transparent;
      opacity: .6;
      cursor: pointer;
      &.isSelected {
        opacity: 1;
        border-color: #ffd952;
      }
      img {
        height: 100%;
      }
    }
  }
  .title {
    padding-left: 20px;
    color: #000;
    font-size: 18px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: .25px;
    background-image: url('https://gvs.weathernews.jp/golf/image/common/icon_arrow.svg');
    background-position: left center;
    background-repeat: no-repeat;
    background-size: 10px;
  }
  .table {
    .row {
      display: flex;
      align-items: stretch;
      border-bottom: 1px solid #E0E6F3;
      border-left: 1px solid #E0E6F3;
      border-right: 1px solid #E0E6F3;
      &:first-child {
        border-top: 1px solid #E0E6F3;
      }
    }
    .item {
      display: flex;
      align-items: center;
      background-color: #F0F3F9;
      width: 130px;
      padding: 5px 10px;
      color: #000;
      font-size: 16px;
      font-weight: 500;
      line-height: 1.4em;
      @media screen and (min-width: 830px) {
        font-size: 18px;
      }
    }
    .text {
      padding: 5px;
      flex: 1;
      color: #000;
      font-size: 16px;
      line-height: 1.4em;
      white-space: pre-wrap;
      border-left: 1px solid #E0E6F3;
      &[data-type='url'] {
        word-wrap: break-word;
        overflow-wrap: break-word;
        word-break: break-all;
      }
      @media screen and (min-width: 830px) {
        font-size: 18px;
      }
    }
  }
}
@media screen and (min-width: 830px) {
  .golfInfo {
    .name {
      font-size: 20px;
    }
  }
}
</style>
